import {
  BillingAccessReasonEnum,
  BillingAccessStateEnum,
  type OrganizationBillingState,
} from '@/gen/cortado/v1/subscriptions_pb'

export const BILLING_SUSPENDED_PATH = '/billing-suspended' as const
export const ONBOARDING_PATH = '/onboarding/organization' as const
export const BILLING_RECOVERY_PATH = '/company?section=subscription' as const
// Where the guard sends a user away from a gate screen that no longer
// applies (e.g. the org's payment resolved, or a suspension was lifted).
// Matches the authenticated landing route in routes/index.tsx.
export const BILLING_GATE_HOME_PATH = '/dashboard' as const

/** True for the onboarding resume URL and every grouped wizard screen under it. */
export function isOnboardingPath(pathname: string): boolean {
  return pathname === ONBOARDING_PATH || pathname.startsWith(`${ONBOARDING_PATH}/`)
}

/**
 * States that already carry a live Cortado subscription, so a second checkout
 * would be wrong. Pure predicate shared by the /start-trial guard and the
 * onboarding subscribe step.
 */
export function alreadyHasLiveSubscription(state: BillingAccessStateEnum | undefined): boolean {
  return (
    state === BillingAccessStateEnum.BILLING_ACCESS_STATE_TRIALING ||
    state === BillingAccessStateEnum.BILLING_ACCESS_STATE_ACTIVE ||
    state === BillingAccessStateEnum.BILLING_ACCESS_STATE_GRACE ||
    state === BillingAccessStateEnum.BILLING_ACCESS_STATE_COMP
  )
}

export type BillingGateRedirectTarget =
  | typeof BILLING_SUSPENDED_PATH
  | typeof ONBOARDING_PATH
  | typeof BILLING_RECOVERY_PATH
  | typeof BILLING_GATE_HOME_PATH

/**
 * Resolves the billing-gate redirect for the current navigation, or null if
 * none is needed. Pure and total so the `_app` `beforeLoad` guard (and its
 * tests) never have to render a router to exercise this branch (R-72, R-83).
 * A null/undefined billing state (not yet loaded, or an infra failure)
 * resolves to no redirect at all - the backend interceptor fails open on
 * infra errors (§3.5) and this client-side guard mirrors that posture
 * instead of bouncing a user around on a transient fetch failure.
 *
 * Priority follows the backend decision: bypass, suspension, onboarding,
 * then the paid-customer billing-only lapse. Never-subscribed post-launch
 * organizations deliberately remain in the navigable read-only shell.
 */
export function resolveBillingGateRedirect(
  pathname: string,
  billingState: OrganizationBillingState | null | undefined
): BillingGateRedirectTarget | null {
  if (!billingState) return null

  if (billingState.enforcementBypassed) return null

  if (
    billingState.accessReason === BillingAccessReasonEnum.SUSPENDED ||
    billingState.isSuspended
  ) {
    return pathname === BILLING_SUSPENDED_PATH ? null : BILLING_SUSPENDED_PATH
  }
  if (pathname === BILLING_SUSPENDED_PATH) {
    return BILLING_GATE_HOME_PATH
  }

  if (billingState.accessReason === BillingAccessReasonEnum.ONBOARDING_INCOMPLETE) {
    return ONBOARDING_PATH
  }

  if (billingState.accessReason === BillingAccessReasonEnum.SUBSCRIPTION_EXPIRED) {
    return pathname === '/company' ? null : BILLING_RECOVERY_PATH
  }

  return null
}
